import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Redirect,
	Route,
	Switch,
	useLocation,
	useHistory
} from 'react-router-dom';
import { Icon } from 'semantic-ui-react';

import { getAgency } from '../../api/nora/agencies';

import { useAuthState } from '../../contexts/AuthContext';
import {
	useAgencyAction,
	useAgencyState
} from '../../contexts/AgenciesContext';
import { useUIAction } from '../../contexts/UIContext';

import useWindowWidth from '../../hooks/useWindowWidth';

import Agency from './Agency';
import Header from '../Header';
import Programs from './Programs';
import Staff from './Staff';
import TimeoutModal from '../TimeoutModal';
import ToDo from './ToDo';
import Admin from './Admin';
// import DashboardHeader from './DashboardHeader';
import Sidebar from './Sidebar';

import styles from './agency-portal.module.css';
import Locations from './Locations';

const AgencyPortal = () => {
	const location = useLocation();
	const history = useHistory();

	const { user } = useAuthState();
	const { setAgency } = useAgencyAction();
	const {
		agency,
		initialized
	} = useAgencyState();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setSidebar
	} = useUIAction();

	const [ loading, setLoading ] = useState( true );

	const width = useWindowWidth();

	useEffect(
		() => {
			if ( width > 1024 ) {
				setSidebar( false );
			}
		}
		, [ setSidebar, width ]
	);

	const handleSidebarOpen = useCallback( () => {
		setSidebar( true );
	}, [ setSidebar ] );

	useEffect( () => {
		if ( location.pathname !== '/profile' ) {
			setLoading( true );

			if ( !initialized ) {
				history.push( '/todo' );
				setInfobarLoader( 'Please wait...' );
			} else {
				setInfobarLoader( 'Retrieving Agency Information...' );
				setInfobar( <span>{ user.profile.organization.name } </span> );

				( async () => {
					try {
						const userAgency = await getAgency( user.portal.id );

						setAgency( userAgency );
					} catch ( error ) {
						setApiErrorAlert( error );
					}

					setInfobarLoader( '' );
					setLoading( false );
				} )();
			}
		}
	}, [
		history,
		initialized,
		location, // run each time route changes
		setAgency,
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		user.profile.organization.name,
		user.portal.id
	] );

	const paths = useMemo( () => {
		const pathsArr = [];

		if ( user.permissions.agencyListStaff.isAvailable ) {
			pathsArr.push( {
				path: '/staff',
				component: Staff
			} );
		}

		pathsArr.push( {
			path: '/agency',
			component: Agency
		} );

		pathsArr.push( {
			path: '/locations',
			component: Locations
		} );

		if ( user.permissions.agencyListPrograms.isAvailable ) {
			pathsArr.push( {
				path: '/programs',
				component: Programs
			} );
		}

		if ( user.permissions.agencyEditPrograms.isAvailable ) {
			pathsArr.push( {
				path: '/todo',
				component: ToDo
			} );
		}

		pathsArr.push( {
			path: '/profile',
			component: Admin
		} );

		return pathsArr;
	}, [ user.permissions ] );

	const mapPaths = useCallback(
		() => paths.map( path => (
			<Route
				key={ path.path }
				{ ...path }
			/>
		) ),
		[ paths ]
	);

	return (
		<>
			<div
				style={{
					alignItems: 'stretch',
					display: 'flex',
					minHeight: '100%'
				}}
			>
				<Sidebar />
				<div style={ { flexGrow: 1, maxWidth: '100%' } }>
					<Header
						className={ styles.header }
						navigation="external"
						OverrideRight={
							width <= 1024
								? () => <Icon
									className={ 'no-print' }
									link
									name="bars"
									onClick={ handleSidebarOpen }
									size="big"
									style={{ margin: '0 0 0 auto' }}
								/>
								: () => null
						}
					/>
					{/* { initialized
						? <DashboardHeader />
						: null
					} */}
					{ agency && !loading
						? <Switch>
							{ mapPaths() }
							<Redirect
								from="*"
								to="/todo"
							/>
						</Switch>
						: null
					}
				</div>
			</div>
			<TimeoutModal />
		</>
	);
};

export default AgencyPortal;
